"use client";

import { memo, useMemo } from "react";
import WritingCard from "@/components/blocks/thoughts/writing-card";
import Search from "@/components/navigation/search";
import { Writing } from "@/lib/types";
import TagFilter from "./tag-filter";

interface ThoughtsContentProps {
  writings: Writing[];
  paginatedWritings: Writing[];
  searchParams: {
    search?: string;
    tags?: string;
    page?: string;
  };
}

const ThoughtsContent = memo(
  ({ writings, paginatedWritings, searchParams }: ThoughtsContentProps) => {
    const search = searchParams.search || "";
    const selectedTags = searchParams.tags?.split(",") || [];

    // Memoize the unique tags computation to avoid recalculating on every render
    const uniqueTags = useMemo(() => {
      return Array.from(
        new Set(
          writings.flatMap(
            (writing: Writing) => writing.metadata.tag?.split(", ") || [],
          ),
        ),
      ).sort() as string[];
    }, [writings]);

    // Memoize the filter function to avoid recalculation unless dependencies change
    const filterWritings = useMemo(() => {
      return (writings: Writing[], search: string, tags: string[]) => {
        const filteredWritings = writings.filter((writing: Writing) => {
          const queries = search.toLowerCase().split(" ");
          let includeMatch = false;
          let removeMatch = false;

          for (let i = 0; i < queries.length; i++) {
            const query = queries[i];
            if (query.startsWith("tag:")) {
              const tag = query.substring(4);
              if (writing.metadata.tag?.toLowerCase().includes(tag)) {
                includeMatch = true;
              } else {
                includeMatch = false;
                break;
              }
            } else if (query.startsWith("-")) {
              const value = query.substring(1);
              if (
                writing.title.toLowerCase().includes(value) ||
                writing.metadata.snippet?.toLowerCase().includes(value) ||
                writing.metadata.tag?.toLowerCase().includes(value)
              ) {
                removeMatch = true;
                break;
              }
            } else {
              if (
                writing.title.toLowerCase().includes(query) ||
                writing.metadata.snippet?.toLowerCase().includes(query) ||
                writing.metadata.tag?.toLowerCase().includes(query)
              ) {
                includeMatch = true;
              } else {
                includeMatch = false;
                break;
              }
            }
          }

          const matchesTags =
            tags.length === 0 ||
            tags.some((tag) => writing.metadata.tag?.includes(tag));

          return includeMatch && !removeMatch && matchesTags;
        });

        return filteredWritings;
      };
    }, []);

    // Memoize the filtered writings to avoid recalculation on every render
    const displayedWritings = useMemo(() => {
      return search || selectedTags.length > 0
        ? filterWritings(writings, search, selectedTags)
        : paginatedWritings;
    }, [search, selectedTags, writings, paginatedWritings, filterWritings]);

    return (
      <>
        <Search domain="thoughts" />
        <TagFilter tags={uniqueTags} />
        <ul role="list" className="mt-8 grid grid-cols-1 gap-8">
          {displayedWritings.length > 0 ? (
            displayedWritings.map((writing: Writing, idx: number) => {
              return (
                <WritingCard
                  key={writing.slug || idx}
                  link={`/thoughts/${writing?.slug}`}
                  title={writing.title}
                  subtitle={writing.metadata.snippet}
                  tag={writing.metadata.tag}
                  date={writing.published_at}
                />
              );
            })
          ) : (
            <p className="col-span-3 text-center">
              Nothing found for{" "}
              <span className="font-serif italic">{search}</span>, try something
              else
            </p>
          )}
        </ul>
      </>
    );
  },
);

ThoughtsContent.displayName = "ThoughtsContent";

export default ThoughtsContent;
